const destinations = [
  {
    name: "Germany",
    context: "Military PCS assignments to Ramstein, Stuttgart, and Wiesbaden; also business travel to Frankfurt and Berlin",
    urgency: "PCS orders often arrive with 14–30 days to report",
    icon: "ri-map-pin-2-line",
    flag: "DE",
  },
  {
    name: "United Kingdom",
    context: "London business meetings, Scottish golf trips, RAF Lakenheath / Mildenhall military assignments",
    urgency: "Executive and military travel frequently has 7–14 day lead time",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "Bahamas & Caribbean",
    context: "JAXPORT cruise departures, family beach vacations, spring break trips from UNF and JU",
    urgency: "Last-minute cruise deals and charter bookings happen 2–4 weeks out",
    icon: "ri-map-pin-2-line",
    flag: "BS",
  },
  {
    name: "Italy",
    context: "Navy port visits, Mediterranean cruises, heritage tourism from Jacksonville's Italian-American community",
    urgency: "Port leave windows and cruise schedules are fixed and non-negotiable",
    icon: "ri-map-pin-2-line",
    flag: "IT",
  },
  {
    name: "Japan & South Korea",
    context: "Pacific Fleet deployments, Yokosuka / Okinawa PCS assignments, Daegu rotations",
    urgency: "Deployment orders have immovable report dates — dependent passports must be current",
    icon: "ri-map-pin-2-line",
    flag: "JP",
  },
  {
    name: "Canada",
    context: "Niagara Falls family trips, Toronto business, Vancouver connections, summer lake vacations",
    urgency: "Summer road trips and last-minute bookings peak May through August",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
];

const commonSituations = [
  {
    stat: "68%",
    label: "of Jacksonville expedited requests are military-related or discovered within 3 weeks of travel",
  },
  {
    stat: "2–4 Weeks",
    label: "average notice Jacksonville travelers have before a planned international trip or PCS",
  },
  {
    stat: "May–Aug",
    label: "peak season when PCS cycles, college study-abroad deadlines, and family vacation bookings collide",
  },
  {
    stat: "72 Hours",
    label: "shortest deployment-leave turnaround we have accommodated for a Mayport Naval family",
  },
];

export default function JacksonvilleDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Jacksonville Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Northeast Florida clients. Understanding local travel behavior is part of why our document review catches issues before they derail your PCS, cruise, or business trip.
          </p>
        </div>

        {/* Stat block row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Destinations grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal linking block */}
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Florida with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/orlando" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Orlando <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/tampa" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tampa <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/boca-raton" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Boca Raton <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}